import React, { useCallback, useEffect, useRef, Fragment } from 'react';
import { BoldIcon, ItalicIcon, UnderlineIcon, Heading1Icon, Heading2Icon, Heading3Icon, ListIcon, ListOrderedIcon, LinkIcon, QuoteIcon, CodeIcon, MinusIcon, Undo2Icon, Redo2Icon, TypeIcon } from 'lucide-react';
interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  label?: string;
  minHeight?: string;
}
interface ToolbarAction {
  icon: React.ElementType;
  command: string;
  arg?: string;
  label: string;
  type?: 'button' | 'separator';
}
const TOOLBAR_GROUPS: ToolbarAction[][] = [[{
  icon: Undo2Icon,
  command: 'undo',
  label: 'Undo'
}, {
  icon: Redo2Icon,
  command: 'redo',
  label: 'Redo'
}], [{
  icon: TypeIcon,
  command: 'formatBlock',
  arg: 'p',
  label: 'Paragraph'
}, {
  icon: Heading1Icon,
  command: 'formatBlock',
  arg: 'h1',
  label: 'Heading 1'
}, {
  icon: Heading2Icon,
  command: 'formatBlock',
  arg: 'h2',
  label: 'Heading 2'
}, {
  icon: Heading3Icon,
  command: 'formatBlock',
  arg: 'h3',
  label: 'Heading 3'
}], [{
  icon: BoldIcon,
  command: 'bold',
  label: 'Bold'
}, {
  icon: ItalicIcon,
  command: 'italic',
  label: 'Italic'
}, {
  icon: UnderlineIcon,
  command: 'underline',
  label: 'Underline'
}], [{
  icon: ListIcon,
  command: 'insertUnorderedList',
  label: 'Bullet List'
}, {
  icon: ListOrderedIcon,
  command: 'insertOrderedList',
  label: 'Numbered List'
}, {
  icon: QuoteIcon,
  command: 'formatBlock',
  arg: 'blockquote',
  label: 'Quote'
}], [{
  icon: LinkIcon,
  command: 'createLink',
  label: 'Insert Link'
}, {
  icon: CodeIcon,
  command: 'formatBlock',
  arg: 'pre',
  label: 'Code Block'
}, {
  icon: MinusIcon,
  command: 'insertHorizontalRule',
  label: 'Divider'
}]];
export function RichTextEditor({
  value,
  onChange,
  label,
  minHeight = '300px'
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalChange = useRef(false);
  // Sync external value changes
  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
    isInternalChange.current = false;
  }, [value]);
  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);
  const execCommand = useCallback((command: string, arg?: string) => {
    editorRef.current?.focus();
    if (command === 'createLink') {
      const url = window.prompt('Enter URL:', 'https://');
      if (url) {
        document.execCommand('createLink', false, url);
      }
      return;
    }
    if (command === 'formatBlock' && arg) {
      document.execCommand('formatBlock', false, '<' + arg + '>');
      return;
    }
    document.execCommand(command, false, arg);
    handleInput();
  }, [handleInput]);
  const isActive = useCallback((command: string, arg?: string): boolean => {
    if (command === 'formatBlock' && arg) {
      return document.queryCommandValue('formatBlock') === arg;
    }
    try {
      return document.queryCommandState(command);
    } catch {
      return false;
    }
  }, []);
  return <div className="space-y-2" data-id="element-64">
      {label && <label className="font-mono text-xs text-muted-foreground uppercase tracking-widest block" data-id="element-65">
          {label}
        </label>}

      <div className="border border-border bg-card overflow-hidden" data-id="element-66">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-0.5 p-2 border-b border-border bg-secondary/30" data-id="element-67">
          {TOOLBAR_GROUPS.map((group, gi) => <Fragment key={gi} data-id="element-68">
              {gi > 0 && <div className="w-px h-6 bg-border mx-1" data-id="element-69" />}
              {group.map(action => <button key={action.command + (action.arg || '')} type="button" onClick={() => execCommand(action.command, action.arg)} className={`flex items-center justify-center w-8 h-8 rounded-sm transition-colors ${isActive(action.command, action.arg) ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-secondary'}`} title={action.label} aria-label={action.label} data-id="element-70">
                  <action.icon className="w-4 h-4" data-id="element-71" />
                </button>)}
            </Fragment>)}
        </div>

        {/* Editor */}
        <div ref={editorRef} contentEditable suppressContentEditableWarning onInput={handleInput} onBlur={handleInput} className="p-4 outline-none font-mono text-sm leading-relaxed prose prose-invert max-w-none" style={{
        minHeight
      }} dangerouslySetInnerHTML={{
        __html: value || ''
      }} data-id="element-72" />
      </div>

      <p className="font-mono text-[10px] text-muted-foreground/50" data-id="element-73">
        Select text and use the toolbar to format. Supports headings, bold,
        italic, lists, links, quotes, and code blocks.
      </p>
    </div>;
}